import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
    Box,
    Chip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';

const CATEGORY_LABELS = {
    update:      '업데이트',
    maintenance: '점검',
    security:    '보안',
    event:       '이벤트',
    general:     '일반',
};

const CATEGORY_COLORS = {
    update:      'primary',
    maintenance: 'warning',
    security:    'error',
    event:       'success',
    general:     'default',
};

const columns = [
    { id: 'title',      label: '내용',  minWidth: 230, align: 'left' },
    { id: 'category',   label: '구분',  minWidth: 80,  align: 'left' },
    { id: 'created_at', label: '등록일', minWidth: 100, align: 'left' },
];

function NoticeTable() {
    const navigate = useNavigate();

    const [rows, setRows]               = useState([]);
    const [total, setTotal]             = useState(0);
    const [page, setPage]               = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading]         = useState(false);
    const [error, setError]             = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        fetch(`/api/announcements?audience=home&page=${page + 1}&limit=${rowsPerPage}`)
            .then(r => r.json())
            .then(data => {
                setRows(data.items || []);
                setTotal(data.total || 0);
            })
            .catch(() => setError('공지사항을 불러오지 못했습니다.'))
            .finally(() => setLoading(false));
    }, [page, rowsPerPage]);

    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(+e.target.value);
        setPage(0);
    };

    const labelDisplayedRows = ({ from, to, count }) =>
        `전체 ${count} 게시물 중 ${from}–${to}`;

    return (
        <Box sx={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ minHeight: 440 }}>
                <Table stickyHeader aria-label="notice table">
                    <TableHead>
                        <TableRow>
                            {columns.map(col => (
                                <TableCell
                                    key={col.id}
                                    align={col.align}
                                    style={{ minWidth: col.minWidth, fontWeight: 900, fontFamily: 'NotoSansKR' }}>
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        ) : error ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                                    <Typography sx={{ fontFamily: 'NotoSansKR', color: blueGrey[400], fontSize: 14 }}>
                                        {error}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                                    <Typography sx={{ fontFamily: 'NotoSansKR', color: blueGrey[400], fontSize: 14 }}>
                                        등록된 공지사항이 없습니다.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : rows.map(row => (
                            <TableRow
                                hover
                                key={row.id}
                                onClick={() => navigate(`/notices/${row.id}`)}
                                sx={{ cursor: 'pointer' }}>
                                <TableCell style={{ fontFamily: 'NotoSansKR' }}>
                                    {row.title}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={CATEGORY_LABELS[row.category] || row.category}
                                        size="small"
                                        color={CATEGORY_COLORS[row.category] || 'default'}
                                        variant="outlined"
                                        sx={{ fontFamily: 'NotoSansKR', fontSize: 11 }}
                                    />
                                </TableCell>
                                <TableCell style={{ fontFamily: 'NotoSansKR', color: blueGrey[500], fontSize: 13 }}>
                                    {row.created_at ? row.created_at.slice(0, 10).replace(/-/g, '. ') + '.' : '-'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                labelRowsPerPage="한 페이지당 게시물 수"
                labelDisplayedRows={labelDisplayedRows}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Box>
    );
}

export default NoticeTable;
